import '../types';
import { mergeAttributes, Node } from '@tiptap/core';
function parseWidth(value) {
    if (!value)
        return null;
    const parsed = parseInt(value, 10);
    return Number.isFinite(parsed) ? parsed : null;
}
function getWidthFromCol(col) {
    if (!col)
        return null;
    const widthAttr = parseWidth(col.getAttribute('width'));
    if (widthAttr)
        return widthAttr;
    const styleAttr = col.getAttribute('style') || '';
    const styleChunks = styleAttr.split(';').map(part => part.trim()).filter(Boolean);
    for (const chunk of styleChunks) {
        const idx = chunk.indexOf(':');
        if (idx === -1)
            continue;
        const key = chunk.slice(0, idx).trim().toLowerCase();
        const val = chunk.slice(idx + 1).trim();
        if (key === 'width') {
            const px = parseWidth(val);
            if (px)
                return px;
        }
    }
    return null;
}
function getCellColumnStartIndex(element) {
    var _a;
    const rowChildren = Array.from(((_a = element.parentElement) === null || _a === void 0 ? void 0 : _a.children) || []);
    let colIndex = 0;
    for (const child of rowChildren) {
        if (child === element)
            break;
        const span = parseInt(child.getAttribute('colspan') || '1', 10);
        colIndex += Number.isFinite(span) && span > 0 ? span : 1;
    }
    return colIndex;
}
/**
 * This extension allows you to create table headers.
 * @see https://www.tiptap.dev/api/nodes/table-header
 */
export const TableHeader = Node.create({
    name: 'tableHeader',
    addOptions() {
        return {
            HTMLAttributes: {},
        };
    },
    content: 'block+',
    addAttributes() {
        return {
            colspan: {
                default: 1,
            },
            rowspan: {
                default: 1,
            },
            background: {
                default: null,
                parseHTML: element => element.getAttribute('data-background') || element.style.backgroundColor || null,
                renderHTML: attributes => {
                    if (!attributes.background)
                        return {};
                    return {
                        'data-background': attributes.background,
                        style: `background-color: ${attributes.background}`,
                    };
                },
            },
            borderColor: {
                default: null,
                parseHTML: element => element.getAttribute('data-border-color') || null,
                renderHTML: attributes => {
                    if (!attributes.borderColor)
                        return {};
                    return {
                        'data-border-color': attributes.borderColor,
                        style: `border-color: ${attributes.borderColor}`,
                    };
                },
            },
            colwidth: {
                default: null,
                parseHTML: element => {
                    var _a;
                    const colwidth = element.getAttribute('colwidth');
                    const value = colwidth ? colwidth.split(',').map(width => parseInt(width, 10)) : null;
                    if (!value) {
                        const cols = (_a = element.closest('table')) === null || _a === void 0 ? void 0 : _a.querySelectorAll('colgroup > col');
                        const colspan = Math.max(parseInt(element.getAttribute('colspan') || '1', 10) || 1, 1);
                        const cellIndex = getCellColumnStartIndex(element);
                        if (cols && cols.length) {
                            const widths = [];
                            for (let i = 0; i < colspan; i += 1) {
                                const width = getWidthFromCol(cols[cellIndex + i] || null);
                                if (!width) {
                                    return null;
                                }
                                widths.push(width);
                            }
                            return widths.length ? widths : null;
                        }
                    }
                    return value;
                },
            },
        };
    },
    tableRole: 'header_cell',
    isolating: true,
    parseHTML() {
        return [{ tag: 'th' }];
    },
    renderHTML({ HTMLAttributes }) {
        const attrs = { ...HTMLAttributes };
        // Width persistence comes from <colgroup>, keep cell markup clean.
        delete attrs.colwidth;
        return ['th', mergeAttributes(this.options.HTMLAttributes, attrs), 0];
    },
});
